import React, { useState, useEffect, useRef } from 'react';
import { Pencil, Check, X } from 'lucide-react';
import { ABLE_COLORS, ENCOURAGING_MESSAGES } from '../../../constants';

/**
 * FocusModeCard - Shows current task in focus mode with flip animation
 * 
 * Features:
 * - Flip card to see notes
 * - Edit mode for title and notes
 * - Action buttons (Not Now / Done)
 * - Encouraging message
 */
export default function FocusModeCard({
  currentTask,
  filteredTasks,
  focusIndex,
  onMarkDone,
  onSkip,
  onUpdateTask
}) {
  const [isCardFlipped, setIsCardFlipped] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [encouragingMessage, setEncouragingMessage] = useState('');
  const titleInputRef = useRef(null);
  
  // Edit buffer
  const [editBuffer, setEditBuffer] = useState({
    title: '',
    notes: ''
  });

  // Pick random encouraging message on mount
  useEffect(() => {
    const msg = ENCOURAGING_MESSAGES[Math.floor(Math.random() * ENCOURAGING_MESSAGES.length)];
    setEncouragingMessage(msg);
  }, []);

  // Reset flip and edit state when task changes
  useEffect(() => {
    setIsCardFlipped(false);
    setIsEditing(false);
  }, [currentTask?.id]);

  // Initialize edit buffer when entering edit mode
  useEffect(() => {
    if (isEditing && currentTask) {
      setEditBuffer({
        title: currentTask.title || '',
        notes: currentTask.notes || ''
      });
      setTimeout(() => titleInputRef.current?.focus(), 50);
    }
  }, [isEditing, currentTask]);

  const handleMarkDone = () => {
    if (currentTask) {
      onMarkDone(currentTask.id);
      setIsCardFlipped(false);
    }
  };

  const handleSkip = () => {
    onSkip();
    setIsCardFlipped(false);
  };

  const handleStartEdit = (e) => {
    e.stopPropagation();
    setIsEditing(true);
  };

  const handleSave = () => {
    if (!currentTask) return;
    
    const updates = {};
    if (editBuffer.title.trim() && editBuffer.title !== currentTask.title) {
      updates.title = editBuffer.title.trim();
    }
    if (editBuffer.notes !== (currentTask.notes || '')) {
      updates.notes = editBuffer.notes;
    }
    
    if (Object.keys(updates).length > 0) {
      onUpdateTask(currentTask.id, updates);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      handleCancel();
    }
  };

  // Empty state
  if (filteredTasks.length === 0) {
    return (
      <div>
        <div style={{
          background: ABLE_COLORS.neutral,
          borderRadius: ABLE_COLORS.cardRadiusLarge,
          padding: '40px 28px',
          boxShadow: ABLE_COLORS.cardShadow,
          minHeight: '220px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          marginBottom: '16px',
          border: `1px solid ${ABLE_COLORS.borderLight}`,
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: `linear-gradient(90deg, ${ABLE_COLORS.primary} 0%, ${ABLE_COLORS.secondary} 100%)`
          }} />
          <div style={{ textAlign: 'center' }}>
            <h2 style={{
              fontSize: '20px',
              color: ABLE_COLORS.textPrimary,
              margin: '0 0 8px 0',
              fontWeight: '600'
            }}>
              No tasks right now
            </h2>
            <p style={{
              fontSize: '14px',
              color: ABLE_COLORS.textMuted,
              margin: 0
            }}>
              Switch to Plan mode to add some
            </p>
          </div>
        </div>
      </div>
    );
  }

  // No current task
  if (!currentTask) return null;

  return (
    <div>
      {/* Focus Card */}
      <div style={{
        background: ABLE_COLORS.neutral,
        borderRadius: ABLE_COLORS.cardRadiusLarge,
        padding: '40px 28px',
        boxShadow: ABLE_COLORS.cardShadow,
        minHeight: '220px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        marginBottom: '16px',
        border: `1px solid ${isEditing ? ABLE_COLORS.primary : ABLE_COLORS.borderLight}`,
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Subtle gradient accent at top */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: `linear-gradient(90deg, ${ABLE_COLORS.primary} 0%, ${ABLE_COLORS.secondary} 100%)`
        }} />

        {/* Edit button */}
        {!isEditing && (
          <button
            onClick={handleStartEdit}
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              background: ABLE_COLORS.bgSubtle,
              border: `1px solid ${ABLE_COLORS.border}`,
              borderRadius: '8px',
              padding: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.15s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = ABLE_COLORS.border;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = ABLE_COLORS.bgSubtle;
            }}
            title="Edit task"
          >
            <Pencil size={16} color={ABLE_COLORS.textMuted} />
          </button>
        )}

        {isEditing ? (
          /* Edit Mode */
          <div onKeyDown={handleKeyDown}>
            {/* Title input */}
            <input
              ref={titleInputRef}
              type="text"
              value={editBuffer.title}
              onChange={(e) => setEditBuffer(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Task title..."
              style={{
                width: '100%',
                padding: '12px 14px',
                border: `1px solid ${ABLE_COLORS.border}`,
                borderRadius: ABLE_COLORS.chipRadius,
                fontSize: '18px',
                fontWeight: '600',
                outline: 'none',
                marginBottom: '16px',
                boxSizing: 'border-box',
                textAlign: 'center'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = ABLE_COLORS.primary;
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = ABLE_COLORS.border;
              }}
            />
            
            {/* Notes textarea */}
            <textarea
              value={editBuffer.notes}
              onChange={(e) => setEditBuffer(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Add notes..."
              rows={4}
              style={{
                width: '100%',
                padding: '12px 14px',
                border: `1px solid ${ABLE_COLORS.border}`,
                borderRadius: ABLE_COLORS.chipRadius,
                fontSize: '14px',
                outline: 'none',
                resize: 'vertical',
                fontFamily: 'inherit',
                boxSizing: 'border-box',
                marginBottom: '16px'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = ABLE_COLORS.primary;
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = ABLE_COLORS.border;
              }}
            />
            
            {/* Save/Cancel buttons */}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button
                onClick={handleCancel}
                style={{
                  padding: '10px 20px',
                  background: ABLE_COLORS.neutral,
                  border: `1px solid ${ABLE_COLORS.border}`,
                  borderRadius: ABLE_COLORS.chipRadius,
                  cursor: 'pointer',
                  color: ABLE_COLORS.textSecondary,
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <X size={16} />
                Cancel
              </button>
              <button
                onClick={handleSave}
                style={{
                  padding: '10px 20px',
                  background: ABLE_COLORS.primary,
                  border: 'none',
                  borderRadius: ABLE_COLORS.chipRadius,
                  cursor: 'pointer',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <Check size={16} />
                Save
              </button>
            </div>
          </div>
        ) : (
          /* Display Mode */
          <div
            onClick={() => setIsCardFlipped(!isCardFlipped)}
            style={{
              textAlign: 'center',
              cursor: 'pointer'
            }}
          >
            {!isCardFlipped ? (
              <>
                {/* Context chips above title */}
                {(currentTask.project || currentTask.area) && (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '8px',
                    marginBottom: '12px'
                  }}>
                    {currentTask.project && (
                      <span style={{
                        fontSize: '11px',
                        fontWeight: '500',
                        padding: '3px 10px',
                        borderRadius: '6px',
                        background: `${ABLE_COLORS.primary}10`,
                        color: ABLE_COLORS.primary
                      }}>
                        {currentTask.project}
                      </span>
                    )}
                    {currentTask.area && (
                      <span style={{
                        fontSize: '11px',
                        fontWeight: '500',
                        padding: '3px 10px',
                        borderRadius: '6px',
                        background: `${ABLE_COLORS.textMuted}15`,
                        color: ABLE_COLORS.textSecondary
                      }}>
                        {currentTask.area}
                      </span>
                    )}
                  </div>
                )}

                <h2 style={{
                  margin: '0 0 16px 0',
                  fontSize: '22px',
                  color: ABLE_COLORS.textPrimary,
                  fontWeight: '600',
                  lineHeight: '1.4'
                }}>
                  {currentTask.title}
                </h2>

                {/* Progress indicator */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}>
                  <span style={{
                    fontSize: '13px',
                    color: ABLE_COLORS.textMuted
                  }}>
                    {focusIndex + 1} of {filteredTasks.length}
                  </span>
                  {/* Visual progress dots */}
                  <div style={{
                    display: 'flex',
                    gap: '4px'
                  }}>
                    {filteredTasks.slice(0, Math.min(5, filteredTasks.length)).map((_, i) => (
                      <div
                        key={i}
                        style={{
                          width: '6px',
                          height: '6px',
                          borderRadius: '50%',
                          background: i === focusIndex ? ABLE_COLORS.primary : ABLE_COLORS.border
                        }}
                      />
                    ))}
                    {filteredTasks.length > 5 && (
                      <span style={{ fontSize: '10px', color: ABLE_COLORS.textMuted }}>+{filteredTasks.length - 5}</span>
                    )}
                  </div>
                </div>

                {currentTask.notes && (
                  <p style={{
                    fontSize: '12px',
                    color: ABLE_COLORS.primary,
                    margin: '16px 0 0 0',
                    fontWeight: '500'
                  }}>
                    Tap to see notes
                  </p>
                )}
              </>
            ) : (
              <>
                <p style={{
                  fontSize: '15px',
                  color: ABLE_COLORS.textSecondary,
                  margin: 0,
                  lineHeight: '1.7',
                  whiteSpace: 'pre-wrap',
                  textAlign: 'left'
                }}>
                  {currentTask.notes || 'No notes yet'}
                </p>
                <p style={{
                  fontSize: '12px',
                  color: ABLE_COLORS.primary,
                  margin: '20px 0 0 0',
                  fontWeight: '500',
                  textAlign: 'center'
                }}>
                  Tap to flip back
                </p>
              </>
            )}
          </div>
        )}
      </div>

      {/* Action buttons - hide when editing */}
      {!isEditing && (
        <>
          <div style={{
            display: 'flex',
            gap: '12px',
            marginBottom: '16px'
          }}>
            <button
              onClick={handleSkip}
              style={{
                flex: 1,
                padding: '16px',
                background: ABLE_COLORS.neutral,
                border: `2px solid ${ABLE_COLORS.border}`,
                borderRadius: ABLE_COLORS.cardRadius,
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                color: ABLE_COLORS.textSecondary,
                transition: 'all 0.15s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = ABLE_COLORS.textMuted;
                e.currentTarget.style.background = ABLE_COLORS.bgSubtle;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = ABLE_COLORS.border;
                e.currentTarget.style.background = ABLE_COLORS.neutral;
              }}
            >
              Not Now
            </button>
            <button
              onClick={handleMarkDone}
              style={{
                flex: 1,
                padding: '16px',
                background: ABLE_COLORS.semantic.success,
                border: 'none',
                borderRadius: ABLE_COLORS.cardRadius,
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                color: 'white',
                transition: 'all 0.15s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = '0.9';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = '1';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              Done!
            </button>
          </div>

          {/* Encouraging message */}
          <p style={{
            textAlign: 'center',
            color: ABLE_COLORS.textMuted,
            fontSize: '13px',
            fontStyle: 'italic',
            margin: 0
          }}>
            {encouragingMessage}
          </p>
        </>
      )}
    </div>
  );
}
